/**
 * Pezzi condivisi dai tab "Reportistica" e "Tratte da recuperare".
 *
 * Le due pagine mostrano gli stessi numeri in forme diverse (percentuali, chilometri,
 * quartieri, date), quindi la formattazione deve essere UNA: due copie divergono al
 * primo ritocco, e si finisce con la stessa tratta scritta in due modi diversi a
 * seconda del tab da cui la guardi.
 */
import { useState, useCallback, useEffect } from "react";
import Select, { components } from "react-select";

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/* ------------------------------------------------------------ numeri --- */

export const nf = (n, d = 0) =>
    n === null || n === undefined || Number.isNaN(n)
        ? "—"
        : n.toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d });

export const pct = (fatto, previsto) => (previsto ? (100 * fatto) / previsto : null);

/* Percentuale gia' formattata. Serve un helper perche' `${nf(pct(a,b),1)}%` stampa
   "—%" quando il denominatore e' zero: un trattino con la percentuale attaccata. */
export const pctTxt = (fatto, previsto, d = 1) => {
    const v = pct(fatto, previsto);
    return v === null ? "—" : `${nf(v, d)}%`;
};

export const km = (metri) => (metri || 0) / 1000;

/* ------------------------------------------------------------ testi --- */

export const titolo = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : "");

/* I quartieri arrivano dall'enum Quartiere (1217) dell'anagrafica tratti, gia' scritti
   per esteso. Solo "San Donato - San Vitale" va accorciato: per intero manda a capo una
   riga su due in tabella e sfonda le etichette del grafico. Gli altri passano com'e'.
   Le chiavi sono le label ESATTE di Interacta: se il cliente le rinomina, qui non
   combaciano piu' e il quartiere viene mostrato per intero — brutto, ma non rotto. */
export const QUARTIERE_LABEL = {
    "San Donato - San Vitale": "S. Donato–S. Vitale",
};
export const quartiere = (q) => QUARTIERE_LABEL[q] || q || "—";

export const formatData = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

/* Classe colore del badge "stato percorso" (workflow Interacta 122). I tre stati
   esecutivi vengono da getPercorsi; "In Lavorazione"/"Da Fare" compaiono nella
   fisarmonica dei giri del giorno e nello storico per tratta. */
export const statoBadgeClass = (stato) => {
    const s = (stato || "").toLowerCase();
    if (s === "percorso non eseguito") return "badge-stato-noneseguite";
    if (s === "percorso parziale") return "badge-stato-parziali";
    if (s === "percorso completato") return "badge-stato-completate";
    if (s === "in lavorazione") return "badge-stato-lavorazione";
    if (s === "da fare") return "badge-stato-dafare";
    return "badge-neutral"; // stati sconosciuti
};

/* ------------------------------------------------- liste di tratte --- */

/* Sposta l'elemento da "from" a "to" restituendo un NUOVO array (non muta l'originale:
   React si accorge del cambiamento solo se cambia il riferimento). Lo splice fa
   esattamente lo "scalamento" voluto: tolgo l'elemento dalla vecchia posizione e lo
   reinserisco nella nuova, tutti quelli in mezzo scorrono di uno da soli.
   Stessa funzione usata inline da TabAnagraficaPercorsi e TabRecuperi. */
export const moveInArray = (arr, from, to) => {
    if (from == null || to == null || from === to || from < 0 || to < 0) return arr;
    if (from >= arr.length || to >= arr.length) return arr;
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

/* Una riga "ha la stella" se è spuntata a mano (t.prioritaria) OPPURE se la tratta è da
   recuperare (recuperoMap[codice], priorità automatica). Valgono uguale per l'ordinamento. */
export const haStella = (t, recuperoMap = {}) => !!t?.prioritaria || !!recuperoMap[t?.codice];

/* Stellate in testa, le altre seguono. Partizione STABILE (due filter, non un sort): dentro
   ciascun gruppo l'ordine del tragitto è preservato. */
export const ordinaPerStelle = (arr, recuperoMap = {}) => [
    ...arr.filter((t) => haStella(t, recuperoMap)),
    ...arr.filter((t) => !haStella(t, recuperoMap)),
];

/* ------------------------------------------------------------- stella --- */

/**
 * Stella di recupero di una tratta: gialla = saltata l'ultima volta, rossa = saltata
 * `volte` volte di fila. `suPost` e' la spunta manuale dell'operatore al "Genera"
 * (sempre gialla), che convive con quella automatica. Unica per tutti i tab, cosi'
 * dicono la stessa cosa nello stesso modo. Niente se non c'e' nessuna stella.
 */
export const Stella = ({ livello, volte = 0, suPost = false }) => {
    if (!livello && !suPost) return null;
    const cls = livello || "gialla";
    const title = livello
        ? `Tratta non effettuata ${volte} volt${volte === 1 ? "a" : "e"} di fila — da recuperare`
        : "Prioritaria su questo percorso (visibile all'autista)";
    return (
        <span className={`gp-star-recupero ${cls}`} title={title} aria-label={title}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
        </span>
    );
};

/* ------------------------------------------------------------ bottoni --- */

/** Tutto schermo per una tabella o per un blocco. */
export const FullscreenBtn = ({ attivo, onClick }) => (
    <button className="gp-fullscreen-btn" onClick={onClick} title={attivo ? "Esci da tutto schermo" : "Tutto schermo"}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {attivo ? (
                <>
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                </>
            ) : (
                <>
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                </>
            )}
        </svg>
    </button>
);

/** Scarica in Excel una sezione della pagina.
    Stile e testo sono quelli degli altri export del progetto (KgTeoriciRaccolti,
    SvuotamentoCampane, PianoFerie): bottone verde `btn-success`, icona di download e
    scritta "Esporta Excel", con "Esportazione…" mentre il file si prepara. */
export const ExportBtn = ({ onExport, titolo: suggerimento }) => {
    const [inCorso, setInCorso] = useState(false);
    const clic = async () => {
        if (inCorso) return;
        setInCorso(true);
        try { await onExport(); } finally { setInCorso(false); }
    };
    return (
        <button className="btn btn-success gp-new-btn" onClick={clic} disabled={inCorso} title={suggerimento}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {inCorso ? "Esportazione…" : "Esporta Excel"}
        </button>
    );
};

/* -------------------------------------------------------- filtri multi --- */

/* Con piu' di due voci selezionate il campo si allargherebbe a fisarmonica e il
   pannello filtri ballerebbe. Dalla terza in poi si mostra "+N", come in TransAutomezzi. */
const MultiCompresso = ({ children, selectProps, data, ...props }) => {
    const scelti = selectProps.value || [];
    const i = scelti.findIndex((v) => v.value === data.value);
    if (i < 2) return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    if (i === 2) return <div className="gp-rs-more">+{scelti.length - 2}</div>;
    return null;
};

/**
 * Una tendina a scelta multipla per i pannelli filtri della pagina.
 * `classNamePrefix="rs"` e' gia' stilizzato in GestionePercorsi.css; il menu esce in
 * un portale per non essere tagliato dal pannello, e resta aperto fra una scelta e
 * l'altra — sceglierne cinque non deve voler dire riaprirla cinque volte.
 */
export const FiltroMulti = ({ options, value, onChange, placeholder = "Tutti" }) => (
    <Select
        classNamePrefix="rs"
        isMulti
        options={options}
        value={value}
        onChange={(v) => onChange(v || [])}
        placeholder={placeholder}
        components={{ MultiValue: MultiCompresso }}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        noOptionsMessage={() => "Nessuna voce"}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000 }) }}
    />
);

/** Da [{value,label}] ai soli valori, e viceversa: il ponte fra react-select e i filtri. */
export const valoriDi = (opts) => (opts || []).map((o) => o.value);
export const opzioniDa = (elenco, etichetta = (v) => String(v)) =>
    elenco.map((v) => ({ value: String(v), label: etichetta(v) }));

/* ---------------------------------------------------------------- KPI --- */

/** Icona di una KPI card: il tratto e la taglia sono quelli delle altre card. */
export const ico = (d) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {d}
    </svg>
);

/** Una card della riga di KPI in cima alla pagina. */
export const KpiCard = ({ tone, icona, label, valore, sub, titolo: suggerimento }) => (
    <div className="stat-card" title={suggerimento || undefined}>
        <div className={`stat-icon ${tone}`}>{icona}</div>
        <div style={{ minWidth: 0 }}>
            <p className="stat-label">{label}</p>
            <p className="stat-value">
                {valore}<span className="gp-rep-statsub">{sub}</span>
            </p>
        </div>
    </div>
);

/* ------------------------------------------------- avviso sui dati --- */

/**
 * Le esecuzioni non sono leggibili: senza, ogni tratta risulta non fatta e le due
 * pagine mostrerebbero — una lo 0% ovunque, l'altra l'intera rete da recuperare —
 * numeri falsi con l'aria di essere veri. Quindi lo si dice prima di ogni altra cosa.
 */
export const AvvisoEsecuzioni = ({ meta, testoDanno }) => {
    if (!meta || meta.sorgenteEsecuzione === "ok") return null;
    return (
        <div className="gp-rep-avviso">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>
                <strong>Le esecuzioni non sono leggibili.</strong>{" "}
                {meta.sorgenteEsecuzione === "non-configurata"
                    ? "Il collegamento al database dell’app degli operatori non è configurato in questo ambiente."
                    : `Nessuno dei ${nf(meta.percorsiConsiderati)} percorsi conclusi risulta nel database dell’app degli operatori.`}{" "}
                {testoDanno}
            </span>
        </div>
    );
};

/** Avviso delle anagrafiche senza campo Zona (task 541): il backend le mostra in tutte e due
 *  le zone perché non si perdano, quindi qui si dice quante sono e cosa fare.
 *  `cosa` = "tratte" | "percorsi". Stile dell'avviso ambra della reportistica. */
export const AvvisoSenzaZona = ({ n, cosa }) => {
    if (!n) return null;
    return (
        <div className="gp-rep-avviso">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>
                <strong>{nf(n)} {cosa} senza Zona.</strong>{" "}
                Compaiono sia nel Levante sia nel Ponente: imposta la Zona su Interacta.
            </span>
        </div>
    );
};

/* ------------------------------------------- report di quartiere --- */

/** Badge dello stato di un report condivisibile (tab "Report di quartiere"). */
const STATO_REPORT_BADGE = {
    bozza: { cls: "badge-neutral", label: "Bozza" },
    pubblicato: { cls: "badge-success", label: "Pubblicato" },
    disattivato: { cls: "badge-warning", label: "Link disattivato" },
};
export const BadgeStatoReport = ({ stato }) => {
    const b = STATO_REPORT_BADGE[stato] || { cls: "badge-neutral", label: stato };
    return <span className={`badge ${b.cls}`}>{b.label}</span>;
};

/** Copia negli appunti con feedback di due secondi; se il browser non lo permette, un prompt. */
export const useCopia = () => {
    const [copiato, setCopiato] = useState(null);
    const copia = useCallback(async (testo, chiave = "x") => {
        try {
            await navigator.clipboard.writeText(testo);
            setCopiato(chiave);
            setTimeout(() => setCopiato(null), 2000);
        } catch (_) {
            window.prompt("Copia il link:", testo);
        }
    }, []);
    return { copia, copiato };
};

/* --------------------------------------------------- chiusura modali --- */

/**
 * Props per l'overlay di un modale: chiude al clic FUORI dal modale, ma solo se anche la
 * PRESSIONE e' partita fuori. Se si preme dentro (selezionando testo, trascinando la mappa)
 * e si rilascia sull'overlay, il browser manda il click all'antenato comune, cioe' proprio
 * all'overlay: il solo controllo target === currentTarget lo scambierebbe per un clic fuori.
 * Dove e' partita la pressione si annota sull'elemento stesso, cosi' non serve un hook in
 * cima a ogni componente.
 * Uso: <div className="modal-overlay" {...clicFuori(() => setAperto(null))}>
 */
export const clicFuori = (onChiudi) => ({
    onMouseDown: (e) => { e.currentTarget.dataset.premutoFuori = e.target === e.currentTarget ? "1" : ""; },
    onClick: (e) => {
        const daFuori = e.currentTarget.dataset.premutoFuori === "1";
        e.currentTarget.dataset.premutoFuori = "";
        if (daFuori && e.target === e.currentTarget) onChiudi();
    },
});

/* -------------------------------------------------------------- foto --- */
/* Foto dei percorsi: galleria del tab Percorsi, editor del report di quartiere, mappa del
   percorso. Le immagini passano dal proxy del backend (GCS non e' pubblico). */

/** URL del proxy immagine; `w` = larghezza richiesta (miniatura), senza = dimensione piena. */
export const fotoImgUrl = (key, w) =>
    `${API_URL}/gestione-percorsi/percorsi/foto-img?key=${encodeURIComponent(key)}${w ? `&w=${w}` : ""}`;

/* Miniatura che non lascia l'icona rotta quando la foto non e' piu' sul bucket
   (scaduta o cancellata dall'app): al suo posto la scritta "non disponibile". */
export const Miniatura = ({ src, alt }) => {
    const [ko, setKo] = useState(false);
    if (ko) return <span className="gp-rq-foto-ko">non disponibile</span>;
    return <img src={src} alt={alt} loading="lazy" onError={() => setKo(true)} />;
};

/**
 * Foto ingrandita con le altre dello stesso gruppo a portata di frecce. Comandi come il
 * lightbox della pagina pubblica (ReportView): frecce, Esc, click fuori per chiudere.
 * `azione(foto)` (facoltativa): il pulsante sulla foto corrente.
 * `src(foto)` restituisce null se la foto non ha piu' il file.
 */
export const AnteprimaFoto = ({ foto, indice, onIndice, onChiudi, src, didascalia, azione }) => {
    const n = foto.length;
    /* In fase di CAPTURE e fermando i tasti usati: sotto puo' esserci una mappa Leaflet col
       focus (mappa del percorso), che su Esc e frecce chiude il popup o sposta la mappa e
       blocca la propagazione. Cosi' i tasti arrivano prima qui, e solo qui. */
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onChiudi();
            else if (e.key === "ArrowRight") { if (n > 1) onIndice((indice + 1) % n); }
            else if (e.key === "ArrowLeft") { if (n > 1) onIndice((indice - 1 + n) % n); }
            else return;
            e.stopPropagation();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [n, indice, onIndice, onChiudi]);

    const f = foto[indice];
    const url = src(f);
    return (
        <div className="gp-lightbox gp-rq-lightbox" onClick={onChiudi} role="dialog" aria-modal="true" aria-label="Foto ingrandita">
            <button type="button" className="gp-lightbox-close" onClick={onChiudi} title="Chiudi" aria-label="Chiudi">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
            {n > 1 && (
                <>
                    <button type="button" className="gp-rq-lightbox-nav prev" aria-label="Foto precedente"
                        onClick={(e) => { e.stopPropagation(); onIndice((indice - 1 + n) % n); }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <button type="button" className="gp-rq-lightbox-nav next" aria-label="Foto successiva"
                        onClick={(e) => { e.stopPropagation(); onIndice((indice + 1) % n); }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                </>
            )}
            <figure className="gp-rq-lightbox-figura" onClick={(e) => e.stopPropagation()}>
                {/* key: senza, lo stato "non disponibile" di una foto rotta resterebbe sulle successive */}
                {url ? <Miniatura key={url} src={url} alt={`foto ${indice + 1} di ${n}`} /> : <span className="gp-rq-foto-ko">non disponibile</span>}
                <figcaption>{didascalia(f)} — foto {indice + 1} di {n}</figcaption>
                {azione && <div className="gp-rq-lightbox-azioni">{azione(f)}</div>}
            </figure>
        </div>
    );
};

/* ---- Validità delle anagrafiche (switch percorsi, ottobre 2026) ----------------------
   Tratte (120) e percorsi (121) hanno "Data inizio/fine validità": il backend calcola per
   ogni riga `validita` = attiva | in_arrivo | archiviata rispetto a oggi, e accetta il
   filtro omonimo. Qui solo la presentazione, la regola vive in anagraficheValiditaService. */

/** Filtro elenchi: cosa si vede di default sono le "correnti" (attive + in arrivo). */
export const FILTRI_VALIDITA = [
    { value: "correnti", label: "Correnti" },
    { value: "in_arrivo", label: "In arrivo" },
    { value: "archiviate", label: "Archiviate" },
    { value: "tutte", label: "Tutte" },
];

/** Stessa regola del backend (estremi inclusi, campo vuoto = nessun limite), per i picker. */
export const attivaIl = (x, data) =>
    !!x && (!x.validoDal || x.validoDal <= data) && (!x.validoAl || data <= x.validoAl);

const periodoTxt = (x) => {
    if (x?.validoDal && x?.validoAl) return `dal ${formatData(x.validoDal)} al ${formatData(x.validoAl)}`;
    if (x?.validoDal) return `dal ${formatData(x.validoDal)}`;
    if (x?.validoAl) return `fino al ${formatData(x.validoAl)}`;
    return "senza limiti";
};

/** Badge di stato validità; per le attive senza date non mostra nulla (il caso normale). */
export const BadgeValidita = ({ riga }) => {
    if (!riga) return null;
    if (riga.validita === "archiviata") return <span className="badge badge-neutral" title={`Valida ${periodoTxt(riga)}`}>Archiviata</span>;
    if (riga.validita === "in_arrivo") return <span className="badge badge-info" title={`Valida ${periodoTxt(riga)}`}>In arrivo dal {formatData(riga.validoDal)}</span>;
    if (riga.validoAl) return <span className="badge badge-warning" title={`Valida ${periodoTxt(riga)}`}>Fino al {formatData(riga.validoAl)}</span>;
    if (riga.validoDal) return <span className="badge badge-success" title={`Valida ${periodoTxt(riga)}`}>Dal {formatData(riga.validoDal)}</span>;
    return <span className="muted">—</span>;
};

/** Segmented control del filtro validità, con i conteggi delle stats del backend. */
export const FiltroValidita = ({ value, onChange, conteggi }) => {
    const n = (v) => {
        if (!conteggi) return null;
        if (v === "correnti") return conteggi.attive + conteggi.inArrivo;
        if (v === "in_arrivo") return conteggi.inArrivo;
        if (v === "archiviate") return conteggi.archiviate;
        return conteggi.attive + conteggi.inArrivo + conteggi.archiviate;
    };
    return (
        <div className="view-toggle-compact" role="group" aria-label="Validità">
            {FILTRI_VALIDITA.map((f) => (
                <button key={f.value} type="button" className={`view-btn-compact ${value === f.value ? "active" : ""}`} onClick={() => onChange(f.value)}>
                    {f.label}{n(f.value) != null ? ` (${n(f.value)})` : ""}
                </button>
            ))}
        </div>
    );
};

/** I due campi data del form crea/modifica (tratta o percorso). */
export const CampiValidita = ({ form, setForm }) => (
    <>
        <div className="form-group">
            <label>Valido dal</label>
            <input type="date" value={form.validoDal || ""} onChange={(e) => setForm((f) => ({ ...f, validoDal: e.target.value }))} />
        </div>
        <div className="form-group">
            <label>Valido fino al</label>
            <input type="date" value={form.validoAl || ""} min={form.validoDal || undefined} onChange={(e) => setForm((f) => ({ ...f, validoAl: e.target.value }))} />
        </div>
        <div className="form-group full">
            <span className="muted" style={{ fontSize: 12 }}>
                Date riferite al giorno del giro, estremi inclusi. Vuote = nessun limite.
            </span>
        </div>
    </>
);
